import React from 'react';
import { ArrowRight, Compass, Sparkles } from 'lucide-react';
import { CardSurface } from './CardSurface';
interface LabStartPanelProps {
  /** Eén beginpunt uit de leerlijn. Ontbreekt als de leerlijn niets oplevert. */
  startSuggestion?: {
    familyId: string;
    title: string;
    module: string;
  };
  onStartSuggestion: (familyId: string) => void;
  onFreePractice: () => void;
}

/**
 * LabStartPanel — wat een leerling ziet zolang we niets van hem weten.
 *
 * Vervangt de twee aanbevelingskaarten bij bewijsniveau `none`. Die kaarten
 * deden op dag één al twee stellige uitspraken ("Beste keuze voor jou", "Je kent
 * het al"), gebaseerd op een leeg `familyMastery`-object.
 *
 * Waarom een eigen paneel en niet gewoon de badges weglaten: de kaarten zijn
 * gebouwd om een KEUZE tussen twee opties voor te leggen. Zonder grond is er
 * geen keuze te maken, en twee gelijkwaardige opties naast elkaar zetten legt
 * dan een beslissing bij een kind die het niet kan nemen. Dit paneel doet in
 * plaats daarvan wat op dag één klopt: uitleggen dat het lab meegroeit, en één
 * duidelijke ingang aanbieden.
 *
 * Het voorstel eronder is expliciet gelabeld als beginpunt uit de leerlijn.
 * Dat is het eerlijke onderscheid: we weten waar de leerstof begint, we weten
 * nog niet waar dít kind begint.
 */
export function LabStartPanel({
  startSuggestion,
  onStartSuggestion,
  onFreePractice
}: LabStartPanelProps) {
  return <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-5 sm:p-6" data-id="element-3196" data-mp="wqe66xip8815">
      <div className="flex items-start gap-3" data-id="element-3197" data-mp="rsuti21ng3bb">
        <span className="mt-0.5 flex h-9 w-9 flex-none items-center justify-center rounded-full bg-indigo-600" data-id="element-3198" data-mp="rpywcfxmlinx">
          <Compass className="h-5 w-5 text-white" aria-hidden="true" data-id="element-3199" data-mp="htvtf7uy9fzx" />
        </span>
        <div className="min-w-0" data-id="element-3200" data-mp="hvzsnchwlqtv">
          <h3 className="text-base font-black text-indigo-900 sm:text-lg" data-id="element-3201" data-mp="37j7jjorx87ys">
            We gaan elkaar nog leren kennen
          </h3>
          <p className="mt-1 text-sm font-medium leading-snug text-indigo-800" data-id="element-3202" data-mp="32ib7up1bvhit">
            Je hebt nog niet geoefend, dus ik weet nog niet wat je goed kunt.
            Kies zelf waar je wilt beginnen — hoe meer je oefent, hoe beter ik je
            kan helpen.
          </p>
        </div>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2" data-id="element-3203" data-mp="1klzo0zn0qtig">
        {startSuggestion && <CardSurface onClick={() => onStartSuggestion(startSuggestion.familyId)} className="group flex h-full flex-col rounded-xl border border-indigo-200 bg-white p-4 text-left shadow-sm hover:shadow-md" data-id="element-3204" data-mp="1pk9h94p6nk1f">
          
            {/* Eerlijk label: dit komt uit de leerlijn, niet uit het kind. */}
            <span className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-indigo-600" data-id="element-3205" data-mp="11wt5bcm861wn">
              <Sparkles className="h-3 w-3" aria-hidden="true" data-id="element-3206" data-mp="16v2xrfgkeoek" />
              Goed om mee te beginnen
            </span>
            <span className="mt-1 block text-base font-black leading-tight text-slate-900" data-id="element-3207" data-mp="rzwrcrdk4nkp">
              {startSuggestion.title}
            </span>
            <span className="mt-0.5 block text-xs font-medium text-slate-500" data-id="element-3208" data-mp="dbwqewdc1eqd">
              {startSuggestion.module}
            </span>
            <span className="mt-auto flex items-center gap-1 pt-3 text-xs font-bold uppercase tracking-wider text-indigo-600" data-id="element-3209" data-mp="9bct9ssreb48">
              Starten
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" data-id="element-3210" data-mp="i9osgj7oyh8a" />
            
            </span>
          </CardSurface>}

        <CardSurface onClick={onFreePractice} className="group flex h-full flex-col rounded-xl border border-indigo-200 bg-white p-4 text-left shadow-sm hover:shadow-md" data-id="element-3211" data-mp="s849ftd08a42">
          
          <span className="text-[10px] font-black uppercase tracking-wider text-slate-500" data-id="element-3212" data-mp="161n3r3754clf">
            Liever zelf kiezen
          </span>
          <span className="mt-1 block text-base font-black leading-tight text-slate-900" data-id="element-3213" data-mp="1fx70frtj3fb8">
            Vrij oefenen
          </span>
          <span className="mt-0.5 block text-xs font-medium text-slate-500" data-id="element-3214" data-mp="1b06tleouu8j2">
            Alle vakken, jij kiest
          </span>
          <span className="mt-auto flex items-center gap-1 pt-3 text-xs font-bold uppercase tracking-wider text-indigo-600" data-id="element-3215" data-mp="34fi85xvu6wxp">
            Bekijk de vakken
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" data-id="element-3216" data-mp="3e875zpygscak" />
            
          </span>
        </CardSurface>
      </div>
    </div>;
}